import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import EducationSection from './EducationSection';
import { INSTAGRAM_URL } from './config';
import './App.css';

/* ── Scroll-triggered fade-in wrapper ── */
function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

/* ── Floating particle background ── */
function ParticleField() {
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    size: 1 + Math.random() * 3.5,
    left: Math.random() * 100,
    delay: Math.random() * 20,
    duration: 12 + Math.random() * 18,
  }));

  return (
    <div className="particle-field" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ── Data ── */
const offerings = [
  { title: 'Stock Trading', desc: 'Real-time analysis, signals, and strategies from elite traders.' },
  { title: 'Crypto', desc: 'Digital assets, DeFi opportunities, and blockchain alpha.' },
  { title: 'Real Estate', desc: 'Exclusive property deals and investment insights.' },
  { title: 'Private Markets', desc: 'Access pre-IPO opportunities and private equity deals.' },
  { title: 'Startup Deals', desc: 'Early-stage investment opportunities before they go mainstream.' },
  { title: 'Venture Capital', desc: 'VC-level deal flow accessible to our members.' },
  { title: 'Angel Investing', desc: 'Direct connections between founders and investors.' },
  { title: 'Education', desc: 'World-class trading and investing education resources.' },
  { title: 'Elite Network', desc: 'Connect with top traders and investors globally.' },
];

const manifesto = [
  'We exist for the ones who refuse to sit on the sidelines.',
  'The traders who stay up studying charts while everyone else is asleep.',
  'The builders who are coding their next venture at 2am.',
  'The investors who see opportunities where others see risk.',
  'The hustlers who know that real wealth isn\'t built by following the crowd \u2014 it\'s built by being in the right rooms, with the right people, at the right time.',
  'Silicon Trades was built because those rooms didn\'t exist.',
  'The kind of community where a trader in New York can connect with an investor in San Francisco, a builder in Miami, and a founder in Tokyo \u2014 all in one place, all moving in the same direction.',
  'Where real trades get shared, real deals get made, and real connections turn into real wealth.',
  'Wall Street built their network behind closed doors.',
  'Silicon Valley built theirs in garages and coffee shops and late night Discords.',
  'We\'re building ours right here.',
  'Stocks. Options. Crypto. Real estate. Startups. Venture. Private markets. Angel deals. Everything serious traders and investors need \u2014 in one elite, global community.',
  'This is Silicon Trades. Built in Silicon Valley. Open to the world. Free to everyone.',
  'The doors are open. Get in while they are and thrive.',
];

const highlightIdx = new Set([4, 12]);
const emphasisIdx = new Set([10]);

function lineClass(i: number) {
  const classes = ['manifesto-line'];
  if (highlightIdx.has(i)) classes.push('manifesto-highlight');
  if (emphasisIdx.has(i)) classes.push('manifesto-emphasis');
  if (i === manifesto.length - 1) classes.push('manifesto-final');
  return classes.join(' ');
}

/* ════════════════════════════════════
   Main App
   ════════════════════════════════════ */
function App() {
  return (
    <div className="app">
      <ParticleField />

      {/* ── Navbar ── */}
      <motion.nav
        className="navbar"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <a href="#home" className="nav-brand">
          ST
        </a>
        <div className="nav-links">
          <a href="#home" className="nav-link">Home</a>
          <a href="#education" className="nav-link">Education</a>
          <a href="#offerings" className="nav-link">Offerings</a>
          <a href="#mission" className="nav-link">Mission</a>
          <a href="#contact" className="nav-link">Contact</a>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="join-btn"
        >
          Join Now
        </a>
      </motion.nav>

      {/* ── Hero ── */}
      <section className="hero" id="home">
        <div className="hero-bg-glow" />
        <div className="hero-inner">
          <motion.div
            className="hero-logo-wrap"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <img
              src="/images/logo-2e98ac.webp"
              alt="Silicon Trades"
              className="hero-logo"
            />
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            TRADE<br />THE MARKETS
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Silicon Valley's most exclusive trading and investing community.
          </motion.p>

          <motion.p
            className="hero-highlight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            Trade Stocks &amp; Crypto
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-cta"
            >
              Join Now &mdash; It's Free
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Free Education ── */}
      <EducationSection />

      {/* ── Offerings ── */}
      <section className="offerings" id="offerings">
        <FadeIn className="offerings-header">
          <h2 className="section-title glow-text">WHAT WE OFFER</h2>
          <p className="section-sub">
            Everything serious traders and investors need &mdash; in one elite
            community.
          </p>
        </FadeIn>

        <div className="offerings-grid">
          {offerings.map((item, i) => (
            <motion.div
              key={item.title}
              className="offering-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
            >
              <h3 className="offering-card-title">{item.title}</h3>
              <p className="offering-card-desc">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Silicon Valley ── */}
      <section className="sv-section">
        <FadeIn>
          <h2 className="section-title glow-text">
            BUILT IN SILICON VALLEY
            <br />
            OPEN TO THE WORLD
          </h2>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="section-sub">
            Join traders &amp; investors from every corner of the globe
          </p>
        </FadeIn>
        <FadeIn delay={0.3}>
          <p className="body-text">
            Whether you're in San Francisco, Miami, New York, or Tokyo, if
            you're serious about building wealth and accessing opportunities
            most people never see, you belong here. Silicon Trades is where the
            next generation of traders, investors, and builders connect,
            collaborate, and win.
          </p>
        </FadeIn>
      </section>

      {/* ── Mission ── */}
      <section className="mission-section" id="mission">
        <FadeIn>
          <h2 className="section-title glow-text">ONE MISSION</h2>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="mission-sub">
            To build the most elite trading and investing network in the world.
          </p>
        </FadeIn>
      </section>

      {/* ── Manifesto ── */}
      <section className="manifesto-section">
        <FadeIn>
          <div className="manifesto-card">
            {manifesto.map((line, i) => (
              <p key={i} className={lineClass(i)}>
                {line}
              </p>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── CTA Banner ── */}
      <section className="cta-banner">
        <FadeIn>
          <h2 className="cta-banner-title">READY TO JOIN?</h2>
          <p className="cta-banner-sub">
            The doors are open. Step inside the most elite trading community
            ever built.
          </p>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-cta"
          >
            Join Silicon Trades
          </a>
        </FadeIn>
      </section>

      {/* ── Contact ── */}
      <section className="contact-section" id="contact">
        <FadeIn>
          <h2 className="section-title glow-text">CONTACT US</h2>
          <p className="section-sub">
            Questions, partnerships, or inquiries.
          </p>
        </FadeIn>
        <FadeIn delay={0.15}>
          <div className="social-row">
            <a
              href="mailto:SilicontradesHQ@gmail.com"
              className="social-link"
            >
              <img src="/images/gmail-logo.png" alt="Email" />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img src="/images/instagram-logo-7d4909.png" alt="Instagram" />
            </a>
            <a
              href="https://x.com/silicontradeshq"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img src="/images/x-logo.png" alt="X / Twitter" />
            </a>
          </div>
          <p className="email-text">SilicontradesHQ@gmail.com</p>
        </FadeIn>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <p>
          &copy; {new Date().getFullYear()} Silicon Trades. All rights
          reserved.
        </p>
      </footer>
    </div>
  );
}

export default App;
